import React, { Fragment, useContext, useMemo } from 'react';
import { Accordion } from '../../../components/containers/Accordion';
import { AccordionTitle } from '../../../components/containers/Accordion/AccordionTitle';
import { SocialProfile } from '../../../components/socialProfiles/SocialProfile';
import { AccordionContent } from '../../../components/containers/Accordion/AccordionContent';
import { profilesHelpers } from '../../Settings/Profiles/Profiles.helpers';
import {
  AccordionErrorMessageType,
  AccordionProviderType
} from '../../../components/containers/Accordion/AccordionProvider';
import { TextEditor } from '../TextEditor';
import { ActiveCustomizeIndexContextProvider } from '../states/ActiveCustomizeIndexProvider';
import { LinkBlock } from '../LinkBlock';
import { FirstComment } from '../FirstComment';
import { TitleBlock } from '../TitleBlock';
import { SocialProfileWithBoardsAndMentionType } from '../helpers/mapSelectedProfilesValues';
import { MediaBlock } from '../MediaBlock';
import { UseFormReturn } from 'react-hook-form';
import { PostStatusSelector } from '../PostStatusSelector';
import { ReadonlyGroupPostAttributesType } from '../../../models/post-editor';
import { PostEditorErrorKeys } from '../../../models/validator-props';
import { DisabledCustomizedPostView } from '../DisabledCustomizedPost/DisabledCustomizedPost.view';
import { IconsEnum } from '../../../constants/icons';
import { InstagramPostTypeBlock } from '../InstagramPostTypeBlock';
import { DisabledCustomizedPostStatusLabel } from '../DisabledCustomizedPostStatusLabel';
import { PostEditorContext } from '../states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { hasSeverity } from '../validators/helpers/severity-checker';
import { Tooltip } from '../../../components/actions/Tooltip';
import { ErrorMessageSeverity } from '../../../constants/form';
import { PostEditorValidationMessages } from '../constants/validation-messages';
import { GoogleButtonBlock } from '../GoogleButtonBlock';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { Debugger } from '../../../components/containers/Debugger';
import scssVars from '../../../assets/scss/exportedVars.module.scss';

export type CustomizeContentPerProfileViewProps = {
  //
  selectedProfiles: SocialProfileWithBoardsAndMentionType[];
  activeIndex?: AccordionProviderType['activeIndex'];
  onTabChange?: AccordionProviderType['onTabChange'];
  formInstance: UseFormReturn;
  readOnlyProfiles: SocialProfileWithBoardsAndMentionType[];
  readOnlyPosts: ReadonlyGroupPostAttributesType[];
};

const POSSIBLE_WARNING_ERRORS_CONTENT: PostEditorErrorKeys[] = [
  'facebookTextCharLimit',
  'facebookTwitterLineBreakLimit',
  'googleMinMedia',
  'googleTextCharLimit',
  'instagramFacebookFirstCommentCharLimit',
  'instagramHashtagLimit',
  'instagramTextCharLimit',
  'linkedinFirstCommentCharLimit',
  'linkedinTextCharLimit',
  'noGifsAllowed',
  'noImagesAllowed',
  'noVideosAllowed',
  'pinterestLineBreakLimit',
  'pinterestTextCharLimit',
  'preventAudioOnlyFiles',
  'tikTokTextCharLimit',
  'tikTokVideoLength',
  'tikTokVideoSize',
  'titleCharLimit',
  'twitterTextCharLimit'
];
const POSSIBLE_ERRORS_CONTENT: PostEditorErrorKeys[] = [
  ...POSSIBLE_WARNING_ERRORS_CONTENT,
  'checkLinkPreviewSuppressed',
  'checkLinksPresent',
  'facebookContentRequired',
  'facebookMediaLimit',
  'finishUpload',
  'googleMediaLimit',
  'instagramMediaLimit',
  'linkedinMediaLimit',
  'mediaRequired',
  'noMixedMedia',
  'pinterestDuplicatesCheck',
  'pinterestMediaLimit',
  'preventTwDuplicate',
  'tikTokMediaLimit',
  'tikTokVideoLength',
  'tikTokVideoSize',
  'twitterMediaLimit',
  'videoNotTranscoded'
];

export const CustomizeContentPerProfileView = ({
  activeIndex,
  onTabChange,
  selectedProfiles,
  readOnlyPosts,
  formInstance,
  readOnlyProfiles
}: CustomizeContentPerProfileViewProps): JSX.Element => {
  const { state } = useContext(PostEditorContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;

  const screenSize = useBreakpoints();
  const isMobile = screenSize === 'small' || screenSize === 'tabletportrait';

  const titleAccordionPrefix = 'title_';
  const errors = JSON.stringify(
    selectedProfiles.flatMap((profile, index) => {
      const errorId = `${titleAccordionPrefix}${profile.id}_${index}`;
      const isCollapsed = activeIndex !== index;
      const hasError = hasSeverity(
        state.postGroups[postGroupIndex].errors,
        ErrorMessageSeverity.error,
        profile.id,
        undefined,
        POSSIBLE_ERRORS_CONTENT
      );

      const hasWarning = hasSeverity(
        state.postGroups[postGroupIndex].errors,
        ErrorMessageSeverity.warning,
        profile.id,
        undefined,
        POSSIBLE_WARNING_ERRORS_CONTENT
      );
      return isCollapsed && (hasError || hasWarning)
        ? { id: errorId, severity: hasError ? ErrorMessageSeverity.error : ErrorMessageSeverity.warning }
        : [];
    })
  );

  // const content = typeof activeIndex === 'number' ? state.postGroups[postGroupIndex].posts[activeIndex] : {};

  const errorsArr: AccordionErrorMessageType[] = useMemo(() => JSON.parse(errors) || [], [errors]);
  return (
    <>
      <Accordion
        collapseAll={true}
        {...(errorsArr.length ? { error: errorsArr } : {})}
        {...(typeof activeIndex === 'number' ? { activeIndex } : {})}
        {...(onTabChange ? { onTabChange } : {})}
        segmentedTitle>
        {selectedProfiles.map((profile, index) => {
          const errorsForThisProfile = errorsArr.filter(e => e.id === `${titleAccordionPrefix}${profile.id}_${index}`);
          return (
            <Fragment key={`${profile.id}_${index}_${!!errorsForThisProfile}`}>
              <AccordionTitle id={`${titleAccordionPrefix}${profile.id}_${index}`} tabindex={index}>
                <div className={'grid-x align-justify'}>
                  <div className={`cell ${isMobile ? '' : 'auto'}`}>
                    <SocialProfile
                      socialProfile={profilesHelpers.remapSocialProfile(profile, false, false, false)}
                      size={'small'}
                      label
                      image={profile.photoUrl}
                      disconnected={!profile.connected}
                    />
                  </div>
                  <div className={'cell shrink'}>
                    <div className={'grid-x align-left align-middle'}>
                      {errorsForThisProfile.length > 0 ? (
                        <div className={'cell shrink'}>
                          <Tooltip
                            icon={
                              !!errorsForThisProfile.find(e => e.severity === ErrorMessageSeverity.error)
                                ? 'alert'
                                : 'warning'
                            }
                            target={`#tooltipTargetMediaBlockWarning_${index}_${postGroupIndex}`}
                            position={'right'}
                            pointer={true}
                            type={'inlined'}
                            autoZIndex={false}
                            style={{ zIndex: scssVars['z-index-overmodal'] }}>
                            <>{PostEditorValidationMessages.expandAccordionTooltip}</>
                          </Tooltip>
                        </div>
                      ) : null}
                      <div className={'cell shrink'}>
                        <ActiveCustomizeIndexContextProvider index={index}>
                          <PostStatusSelector
                            key={`${profile.id}_${index}`}
                            formInstance={formInstance}
                            customizeType={'content'}
                            transparent
                          />
                        </ActiveCustomizeIndexContextProvider>
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionTitle>
              <AccordionContent
                id={`content_${profile.id}_${index}`}
                titleId={`${titleAccordionPrefix}${profile.id}_${index}`}
                borderless>
                <ActiveCustomizeIndexContextProvider index={typeof activeIndex === 'number' ? activeIndex : undefined}>
                  {typeof activeIndex === 'number' ? (
                    <Debugger
                      tooltipContent={`Post for profile: ${profile.id} with activeIndex: ${activeIndex}`}
                      modalContent={state.postGroups[postGroupIndex].posts[activeIndex]}
                    />
                  ) : null}
                  <TextEditor />
                  <FirstComment />
                  <GoogleButtonBlock formInstance={formInstance} />
                  <LinkBlock />
                  <InstagramPostTypeBlock formInstance={formInstance} />
                  <MediaBlock formInstance={formInstance} />
                  <TitleBlock />
                  {/*<pre>{JSON.stringify(content, undefined, 4)}</pre>*/}
                </ActiveCustomizeIndexContextProvider>
              </AccordionContent>
            </Fragment>
          );
        })}
      </Accordion>
      {readOnlyPosts.map((post, index) => {
        const readonlyProfile = readOnlyProfiles.find(p => p.id === post.customizedProfileId && post.active);
        return readonlyProfile ? (
          <DisabledCustomizedPostView
            key={`${post.customizedProfileId}_${selectedProfiles.length + index}`}
            isFirst={!selectedProfiles.length && index === 0}
            accordionIcon={IconsEnum.arrowDown}
            tabindex={selectedProfiles.length + index}>
            <div className={'grid-x align-justify'}>
              <div className={'cell shrink'}>
                <SocialProfile
                  disabled={true}
                  socialProfile={profilesHelpers.remapSocialProfile(readonlyProfile, false, false, false)}
                  size={'small'}
                  label
                  image={readonlyProfile.photoUrl}
                  disconnected={!readonlyProfile.connected}
                />
              </div>
              <div className={'cell shrink'}>
                <DisabledCustomizedPostStatusLabel post={post} />
              </div>
            </div>
          </DisabledCustomizedPostView>
        ) : null;
      })}
    </>
  );
};
